// FieldVision - MarkerMap.tsx
// Leaflet kort der viser alle marker som polygoner med labels
//
// SCOPE: Denne komponent bruges KUN i MineMarkerOversigt.tsx
//        Den bruges IKKE i BookingWizard eller andre komponenter.
//
// FIX Issue 1: Zoom-loaded marker vises med BLAA farve
// FIX 2025-12-31: onFieldClick sender CVR-Marknr for unik identifikation
// FIX 2025-12-31 v3: isSelected + isHovered bruger CVR-Marknr for unik highlight

import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import SprayWeatherWidget from './SprayWeatherWidget';
interface Field {
  Id: number;
  Marknr: string;
  CVR: string;
  IMK_areal: number;
  Geometry?: string;
  CenterLat?: number;
  CenterLon?: number;
  isZoomLoaded?: boolean;
}

interface MarkerMapProps {
  fields: Field[];
  onFieldClick: (fieldKey: string) => void;
  selectedMarknr?: string | null;
  onFieldHover?: (marknr: string | null) => void;
  hoveredMarknr?: string | null;
}

const DENMARK_CENTER: [number, number] = [56.0, 10.0];
const DEFAULT_ZOOM = 7;
const FIELD_ZOOM = 14;

const calculateBounds = (fields: Field[]): [[number, number], [number, number]] | null => {
  if (fields.length === 0) return null;

  let minLat = Infinity, maxLat = -Infinity;
  let minLon = Infinity, maxLon = -Infinity;

  for (const field of fields) {
    if (!(field as any).Geometry) continue;

    try {
      const geometry = JSON.parse((field as any).Geometry);
      const coords = (geometry as any).type === 'MultiPolygon'
        ? (geometry as any).coordinates.flat(2)
        : (geometry as any).coordinates.flat(1);

      for (const coord of coords) {
        if (Array.isArray(coord) && coord.length >= 2) {
          const lon = coord[0];
          const lat = coord[1];
          if (typeof lat === 'number' && typeof lon === 'number') {
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLon = Math.min(minLon, lon);
            maxLon = Math.max(maxLon, lon);
          }
        }
      }
    } catch (e) {
      // Ignorer parse fejl
    }
  }

  if (minLat === Infinity) return null;
  return [[minLat, minLon], [maxLat, maxLon]];
};

const ZoomListener: React.FC<{ onZoomChange: (zoom: number) => void }> = ({ onZoomChange }) => {
  const map = useMapEvents({
    zoomend: () => onZoomChange(map.getZoom()),
    load: () => onZoomChange(map.getZoom())
  });

  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);

  return null;
};

const MapBoundsUpdater: React.FC<{ fields: Field[] }> = ({ fields }) => {
  const map = useMap();

  useEffect(() => {
    if (fields.length === 0) {
      map.setView(DENMARK_CENTER, DEFAULT_ZOOM);
      return;
    }

    const bounds = calculateBounds(fields);
    if (bounds) {
      try {
        const L = (window as any).L;
        const leafletBounds = L.latLngBounds(
          [bounds[0][0], bounds[0][1]],
          [bounds[1][0], bounds[1][1]]
        );
        map.fitBounds(leafletBounds, { padding: [50, 50], maxZoom: FIELD_ZOOM });
      } catch (e) {
        console.error('Error fitting bounds:', e);
      }
    }
  }, [fields, map]);

  return null;
};

const MarkerMap: React.FC<MarkerMapProps> = ({
  fields,
  onFieldClick,
  selectedMarknr,
  onFieldHover,
  hoveredMarknr
}) => {
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);
  // FIX v3: Intern hover state bruger CVR-Marknr format
  const [internalHoveredFieldId, setInternalHoveredFieldId] = useState<string | null>(null);
  // FIX v3: layerRefs bruger CVR-Marknr som key
  const layerRefs = useRef<Map<string, any>>(new Map());

  // FIX v3: activeHoveredFieldId er CVR-Marknr format
  const activeHoveredFieldId = hoveredMarknr ?? internalHoveredFieldId;

  const handleZoomChange = useCallback((zoom: number) => {
    setZoomLevel(zoom);
  }, []);

  // Konverter fields til GeoJSON
  const geoJsonData = useMemo(() => {
    const features = fields
      .filter((f: any) => (f as any).Geometry)
      .map((field: any) => {
        try {
          const geometry = JSON.parse((field as any).Geometry!);
          return {
            type: 'Feature' as const,
            geometry,
            properties: {
              Marknr: (field as any).Marknr,
              IMK_areal: (field as any).IMK_areal,
              CVR: (field as any).CVR,
              Id: (field as any).Id,
              isZoomLoaded: (field as any).isZoomLoaded || false
            }
          };
        } catch (e) {
          return null;
        }
      })
      .filter(Boolean);

    return { type: 'FeatureCollection' as const, features };
  }, [fields]);

  // FIX v3: Style funktion bruger CVR-Marknr for unik matching
  const getStyle = (feature: any) => {
    const cvr = (feature as any)?.properties?.CVR;
    const marknr = (feature as any)?.properties?.Marknr;
    const featureFieldId = `${cvr}-${marknr}`;

    // FIX v3: Sammenlign med CVR-Marknr for unik highlight
    const isSelected = featureFieldId === selectedMarknr;
    const isHovered = featureFieldId === activeHoveredFieldId;
    const isZoomLoaded = (feature as any)?.properties?.isZoomLoaded;

    // Blaa farver for zoom-loaded, groenne for CVR marker
    if (isZoomLoaded) {
      return {
        fillColor: '#a0532a',
        fillOpacity: isSelected ? 0.9 : isHovered ? 0.6 : 0.3,
        color: '#a0532a',
        weight: isSelected ? 3 : isHovered ? 3 : 2,
      };
    }

    return {
      fillColor: '#4783b6',
      fillOpacity: isSelected ? 0.9 : isHovered ? 0.6 : 0.3,
      color: '#4783b6',
      weight: isSelected ? 3 : isHovered ? 3 : 2,
    };
  };

  // FIX v3: Tooltip visibility bruger CVR-Marknr
  // FIX 2026-01-02: Fjernet "show all labels at zoom 14" - nu kun hover-tooltips
  useEffect(() => {
    layerRefs.current.forEach((layer: any, fieldId: string) => {
      const tooltip = layer.getTooltip();
      if (!tooltip) return;

      // Kun vis tooltip for hovered field (ikke alle ved høj zoom)
      const shouldShow = fieldId === activeHoveredFieldId;
      if (shouldShow) {
        layer.openTooltip();
      } else {
        layer.closeTooltip();
      }
    });
  }, [activeHoveredFieldId]);

  const onEachFeature = (feature: any, layer: any) => {
    const marknr = (feature as any).properties?.Marknr;
    const cvr = (feature as any).properties?.CVR;
    const areal = (feature as any).properties?.IMK_areal;
    const isZoomLoaded = (feature as any).properties?.isZoomLoaded;

    // FIX v3: Generer unik CVR-Marknr key
    const fieldKey = `${cvr}-${marknr}`;

    // FIX v3: Brug fieldKey som layer ref key
    if (fieldKey) {
      layerRefs.current.set(fieldKey, layer);
    }

    const arealColor = isZoomLoaded ? '#3b82f6' : '#16a34a';

    if (marknr) {
      layer.bindTooltip(
        `<strong>${marknr}</strong><br/>${(areal as number)?.toFixed(2) || '?'} ha`,
        {
          permanent: false,
          sticky: true,
          direction: 'top',
          className: 'bg-white border border-gray-300 rounded shadow-lg p-2 text-sm'
        }
      );
    }

    layer.on({
      click: () => {
        onFieldClick(fieldKey);
      },
      mouseover: (e: any) => {
        // FIX v3: Brug fieldKey for unik hover
        setInternalHoveredFieldId(fieldKey);
        onFieldHover?.(fieldKey);

        layer.openTooltip();
        e.target.setStyle({
          fillOpacity: 0.6,
          fillColor: isZoomLoaded ? '#a0532a' : '#4783b6',
          weight: 3
        });
      },
      mouseout: (e: any) => {
        setInternalHoveredFieldId(null);
        onFieldHover?.(null);
        layer.closeTooltip();

        // FIX v3: Sammenlign med fieldKey for korrekt reset
        const isSelected = fieldKey === selectedMarknr;
        if (isZoomLoaded) {
          e.target.setStyle({
            fillColor: '#a0532a',
            fillOpacity: isSelected ? 0.9 : 0.3,
            weight: isSelected ? 3 : 2
          });
        } else {
          e.target.setStyle({
            fillColor: '#4783b6',
            fillOpacity: isSelected ? 0.9 : 0.3,
            weight: isSelected ? 3 : 2
          });
        }
      }
    });
  };

  const initialCenter = useMemo(() => {
    if (fields.length === 0) return DENMARK_CENTER;
    const bounds = calculateBounds(fields);
    if (bounds) {
      return [(bounds[0][0] + bounds[1][0]) / 2, (bounds[0][1] + bounds[1][1]) / 2] as [number, number];
    }
    return DENMARK_CENTER;
  }, []);

  const initialZoom = fields.length === 0 ? DEFAULT_ZOOM : FIELD_ZOOM;

  useEffect(() => {
    layerRefs.current.clear();
  }, [fields]);

  return (
    <div className="marker-map" style={{ position: 'relative' }}>
      <MapContainer
        center={initialCenter}
        zoom={initialZoom}
        style={{ height: '100%', width: '100%' }}
        className="marker-leaflet-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <ZoomListener onZoomChange={handleZoomChange} />
        {(geoJsonData as any).features.length > 0 && (
          <>
            <GeoJSON
              data={geoJsonData as any}
              style={getStyle}
              onEachFeature={onEachFeature}
              key={`fields-${fields.length}-${selectedMarknr || 'none'}-${zoomLevel}`}
            />
            <MapBoundsUpdater fields={fields} />
          </>
        )}
      </MapContainer>
        <SprayWeatherWidget fieldCenters={fields.filter(f => f.CenterLat && f.CenterLon).map(f => ({ lat: f.CenterLat!, lon: f.CenterLon! }))} />

      <div className="zoom-indicator">
        Zoom: {zoomLevel.toFixed(0)}
      </div>

      {fields.length === 0 && (
        <div className="map-placeholder">
          <div className="placeholder-content">
            <p>Ingen marker at vise paa kortet</p>
          </div>
        </div>
      )}

      <style>{`
        .marker-map { height: 100%; width: 100%; position: relative; }
        .marker-leaflet-map { height: 100%; width: 100%; z-index: 1; }
        .map-placeholder { position: absolute; top: 0; left: 0; right: 0; bottom: 0; display: flex; align-items: center; justify-content: center; background: #f3f4f6; z-index: 0; }
        .placeholder-content { text-align: center; color: #9ca3af; }
        .zoom-indicator { position: absolute; bottom: 20px; left: 20px; background: rgba(255, 255, 255, 0.9); padding: 6px 12px; border-radius: 4px; font-size: 12px; color: #374151; z-index: 1000; box-shadow: 0 1px 4px rgba(0,0,0,0.1); }
        .field-label-tooltip-transparent { background: transparent !important; border: none !important; box-shadow: none !important; padding: 0 !important; }
        .field-label-tooltip-transparent::before { display: none !important; }
        .field-tooltip-content { display: flex; flex-direction: column; align-items: center; gap: 1px; text-align: center; }
        .field-tooltip-content strong { color: #111827; font-size: 12px; font-weight: 600; text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff, 0 0 4px rgba(255,255,255,0.9); }
        .field-tooltip-content span { font-size: 11px; font-weight: 500; text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff, 0 0 4px rgba(255,255,255,0.9); }
        .leaflet-control-zoom { border: none !important; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important; }
        .leaflet-control-zoom a { width: 36px !important; height: 36px !important; line-height: 36px !important; font-size: 18px !important; color: #374151 !important; }
        .leaflet-control-zoom a:hover { background: #f3f4f6 !important; }
      `}</style>
    </div>
  );
};

export default MarkerMap;
